/**
 * 不存在知识详情时的变更，基于消息内容进行调整
 */
import { useTranslate } from '@tolgee/react';
import { Button, Divider, message as antdMessage } from 'antd';
import { useAdjustRelateKnowledge } from './useAdjustRelateKnowledge';
import { PureBuyerQuestion } from '../Detail/BuyerQuestion';
import { AIReply, PureAIReply } from '../Detail/AIReply';
import {
  SaveKnowledge,
  saveSupplementalKnowledge,
  submitSupplementKnowledge,
  SubmitSupplementKnowledgeBody,
  SupplementKnowledgeAnswer,
  SupplementKnowledgeDetailResponse,
  SupplementKnowledgeValueMessage,
  testMercadoQaKnowledge,
  testSupplementalKnowledge,
  TestSupplementalKnowledgeData,
} from 'services/api/supplementKnowledge';
import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import CiteKnowledge from '../CiteKnowledge';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { concat, pick, toNumber } from 'lodash-es';
import { SubmitAnswer, Action } from './SubmitAnswer';
import { useMemoizedFn } from 'ahooks';
import { messageToAnswer, messageToAnswerV2 } from 'components/Dialogue/components/QAPair/utils';
import { ProductInfo } from './ProductInfo';
import { v4 as uuidv4 } from 'uuid';
import { GenerateLoading } from '../Detail/GenerateLoading';
import { eventBus } from 'services/eventBus';
import { useGetQAPairQueryAIReplyMsgMap } from 'hooks/business/im/useQAPairAIReply';
import { useGetMessageById } from 'hooks/business/message';

interface Props {
  message?: Conversation.Message;
  conversation?: Conversation.Conversation;
  knowledgeQuery: Util.PartialRequired<
    Partial<Conversation.KnowledgeQueryMessage>,
    'fromUserName' | 'region' | 'channel' | 'storeId'
  >;
  externalQuestionId?: string;
  supplementKnowledgeDetail: SupplementKnowledgeDetailResponse;
  onDone?: () => void;
}

export const ContentWithMessage = (props: Props) => {
  const {
    message,
    conversation,
    knowledgeQuery,
    externalQuestionId,
    supplementKnowledgeDetail,
    onDone,
  } = props;
  const { t } = useTranslate();
  const language = useSystemLanguageValue();
  const { buyerNick, region, channel, storeId } = useMemo(() => {
    return {
      buyerNick: conversation ? conversation.buyerNick : knowledgeQuery.fromUserName,
      ...pick(conversation || knowledgeQuery, 'region', 'channel', 'storeId'),
    };
  }, [conversation, knowledgeQuery]);
  const { hide: hideAdjustRelateKnowledge } = useAdjustRelateKnowledge();
  const getQAPairQueryAIReplyMsgMap = useGetQAPairQueryAIReplyMsgMap();
  const getMessageById = useGetMessageById();

  const [action, setAction] = useState<Action>('NONE');
  const [inputAnswers, setInputAnswers] = useState<SupplementKnowledgeAnswer[]>([]);
  const [knowledgeUpdate, setKnowledgeUpdate] = useState<SaveKnowledge[]>([]);
  const [selectedMessages, setSelectedMessages] = useState<Conversation.Message[]>([]);

  const [skuSelected, setSkuSelected] = useState<string[]>([]);
  const [testData, setTestData] = useState<TestSupplementalKnowledgeData | undefined>();

  const aiReplyRef = useRef<HTMLDivElement>(null);
  const [aiReplyFlashKey, setAiReplyFlashKey] = useState(0);

  const { mutateAsync: testSubmit, isLoading: _isTestLoading } = useMutation({
    mutationFn: testSupplementalKnowledge,
    onError: () => {
      antdMessage.error(t('translation.操作失败，请稍后再试'));
    },
  });

  const { mutateAsync: testMercadoSubmit, isLoading: isMercadoTestLoading } = useMutation({
    mutationFn: testMercadoQaKnowledge,
    onError: () => {
      antdMessage.error(t('translation.操作失败，请稍后再试'));
    },
  });

  const isTestLoading = _isTestLoading || isMercadoTestLoading;

  const aiReply = useMemo(() => {
    return testData || supplementKnowledgeDetail.aiReply;
  }, [testData, supplementKnowledgeDetail.aiReply]);

  const hasInputValue = useCallback(() => {
    if (inputAnswers.length > 0) {
      const textAnswers = inputAnswers.filter((answer) => answer.msgType === 'TEXT');
      const mediaAnswers = inputAnswers.filter((answer) => answer.msgType !== 'TEXT');
      return (
        textAnswers.every((answer) => answer.message?.trim() !== '') || mediaAnswers.length > 0
      );
    }
    return false;
  }, [inputAnswers]);

  const scrollToTop = () => {
    if (aiReplyRef.current) {
      aiReplyRef.current.scrollIntoView({
        block: 'center',
        inline: 'center',
        behavior: 'smooth',
      });
      setAiReplyFlashKey((key) => key + 1);
    }
  };

  const canSubmit = useMemo(() => {
    if (knowledgeUpdate.length > 0) {
      return true;
    }
    if (action === 'INPUT') {
      return hasInputValue();
    }
    if (action === 'SELECT' && selectedMessages.length > 0) {
      return true;
    }
    return false;
  }, [knowledgeUpdate.length, action, selectedMessages.length, hasInputValue]);

  const renderBuyerQuestion = () => {
    const { textContent, rewriteQuery } = knowledgeQuery;
    let questionText = '';
    let questionTranslate = '';
    if (rewriteQuery) {
      questionText = rewriteQuery;
    } else {
      questionText = textContent?.text || '';
      questionTranslate =
        textContent?.translationContent?.[sysLangToTranslationLang(language)]?.text || '';
    }
    return (
      <PureBuyerQuestion
        buyerNick={buyerNick}
        question={questionText}
        questionTranslate={questionTranslate}
        extra={null}
      />
    );
  };

  const renderAIReply = useCallback(() => {
    if (!!aiReply) {
      return <AIReply aiReply={aiReply} flashKey={aiReplyFlashKey} />;
    } else if (message) {
      const { textContent, sendTime } = message;
      const text = textContent?.text || '';
      const textTranslate =
        textContent?.translationContent?.[sysLangToTranslationLang(language)]?.text || '';
      return <PureAIReply text={text} textTranslate={textTranslate} time={toNumber(sendTime)} />;
    }
  }, [aiReply, aiReplyFlashKey, message, language]);

  const renderProductInfo = useCallback(() => {
    const query = knowledgeQuery;
    const itemInfo = query.itemInfo!;
    const { itemName, unit, imageUrl, skuInfo, originalHighPrice } = itemInfo;
    return (
      <ProductInfo
        spuId={itemInfo.externalItemId}
        title={itemName}
        price={originalHighPrice}
        unit={unit}
        imageUrl={imageUrl?.[0]}
        skuInfo={skuInfo}
        skuSelected={skuSelected}
        onSKUChange={setSkuSelected}
      />
    );
  }, [knowledgeQuery, skuSelected]);

  const getTestKnowledgeList = (): SaveKnowledge | undefined => {
    if (action === 'SELECT' && selectedMessages.length > 0) {
      const { textContent } = knowledgeQuery;
      const { externalItemId, shopId, skuInfo } = knowledgeQuery.itemInfo!;
      const skus = skuInfo
        ?.filter((sku) => skuSelected.includes(sku.skuId))
        ?.map((sku) => ({
          skuId: sku.skuId,
          skuName: sku.skuName || '',
          sellerSku: sku.sellerSku,
        }));
      return {
        key: textContent?.text || '',
        value: messageToAnswer(selectedMessages),
        source: '客服提交答案',
        bindItem: [
          {
            spuId: externalItemId,
            platform: channel.toUpperCase() as General.PlatformUpperCase,
            region: region.toUpperCase() as General.UpperCaseRegion,
            shopId,
            skus,
          },
        ],
        operate: 'CREATE',
        pointId: uuidv4(),
      };
    }
    if (action === 'INPUT' && inputAnswers.length > 0) {
      const { textContent } = knowledgeQuery;
      const { externalItemId, shopId, skuInfo } = knowledgeQuery.itemInfo!;
      const skus = skuInfo
        ?.filter((sku) => skuSelected.includes(sku.skuId))
        ?.map((sku) => ({
          skuId: sku.skuId,
          skuName: sku.skuName || '',
          sellerSku: sku.sellerSku,
        }));
      return {
        key: textContent?.text || '',
        value: inputAnswers,
        source: '客服提交答案',
        bindItem: [
          {
            spuId: externalItemId,
            platform: channel.toUpperCase() as General.PlatformUpperCase,
            region: region.toUpperCase() as General.UpperCaseRegion,
            shopId,
            skus,
          },
        ],
        operate: 'CREATE',
        pointId: uuidv4(),
      } as SaveKnowledge;
    }
  };

  const handleTest = async () => {
    const questionMsgId = knowledgeQuery.msgId;
    const testKnowledge = getTestKnowledgeList();
    const knowledgeList = testKnowledge
      ? concat(testKnowledge, knowledgeUpdate || [])
      : knowledgeUpdate;
    const response = await (questionMsgId
      ? testSubmit({
          storeId,
          msgId: questionMsgId,
          knowledgeList,
        })
      : testMercadoSubmit({
          externalQuestionId: externalQuestionId!,
          knowledgeList,
        }));
    setTestData(response.data.data);
    eventBus.emit('CITE_KNOWLEDGE_SCROLL');
    scrollToTop();
  };

  /** 不存在，走创建逻辑 */
  const saveKnowledgeWhenCaseNoExists = useMemoizedFn(async () => {
    const query = knowledgeQuery;
    const itemInfo = query.itemInfo!;
    const { externalItemId } = itemInfo;
    const isCiteKnowledgeChanged = knowledgeUpdate.length > 0;

    const botQaMap: Record<string, SupplementKnowledgeValueMessage[]> = {};
    const aiReplyMsgIds = query.msgId && getQAPairQueryAIReplyMsgMap(query.msgId);
    if (aiReplyMsgIds && aiReplyMsgIds.size > 0 && query.msgId) {
      const list = Array.from(aiReplyMsgIds)
        .map((item) => getMessageById(item))
        .filter((item) => !!item);
      botQaMap[query.msgId] = messageToAnswerV2(list);
    }
    const originQuestions = messageToAnswerV2([query]).map((item) => ({
      ...item,
      isBotReply: item.messageId ? botQaMap[item.messageId]?.length > 0 : false,
    }));

    let originAnswers: SupplementKnowledgeValueMessage[] = [];
    let rewriteAnswers: SupplementKnowledgeValueMessage[] | undefined = undefined;

    let manualReviewStatus: SubmitSupplementKnowledgeBody['manualReviewStatus'] = undefined;
    if (action === 'SELECT') {
      originAnswers = messageToAnswerV2(selectedMessages);
      if (originAnswers.length === 0) {
        manualReviewStatus = 'REVIEW_FAILED';
      }
    }
    if (action === 'INPUT') {
      if (hasInputValue()) {
        rewriteAnswers = inputAnswers.map((item) => ({
          ...item,
          isBotReply: false,
        }));
      } else {
        manualReviewStatus = 'REVIEW_FAILED';
      }
    }
    if (action === 'NONE') {
      manualReviewStatus = 'REVIEW_FAILED';
    }
    await (conversation
      ? submitSupplementKnowledge({
          conversationId: conversation?.conversationId,
          storeId,
          spuId: externalItemId,
          skuIds: skuSelected,
          supplementalKnowledgeValue: {
            buyerNick: buyerNick || 'unknown',
            originAnswers,
            originQuestions,
            botQaMap,
            rewriteAnswers,
          },
          manualReviewStatus,
          modify: isCiteKnowledgeChanged,
          citeKnowledgeSaveReq: {
            knowledgeList: knowledgeUpdate,
            testReqId: testData?.testReqId,
          },
        })
      : saveSupplementalKnowledge({
          externalQuestionId,
          knowledgeList: knowledgeUpdate,
          testReqId: testData?.testReqId,
        }));
  });

  const { isLoading: isSaveLoading, mutateAsync: saveContent } = useMutation({
    mutationFn: saveKnowledgeWhenCaseNoExists,
    onSuccess: () => {
      antdMessage.success(t('已提交'));
      onDone?.();
    },
    onError: () => {
      antdMessage.error(t('translation.操作失败，请稍后再试'));
    },
  });

  return (
    <div className="relative w-full h-full flex flex-col">
      {isTestLoading && <GenerateLoading />}
      <div className="bg-[var(--fett-erp-card-bg)] flex-1 pt-4 px-4 pb-[72px] overflow-y-auto">
        <div className="bg-[var(--fett-bg-elevated)]">
          {renderProductInfo()}
          <div className="mt-4" />
          {renderBuyerQuestion()}
          <div className="mt-4" />
          <div ref={aiReplyRef}>{renderAIReply()}</div>
          <Divider className="!my-4" />
          <CiteKnowledge
            applySkuIds={skuSelected}
            conversation={{ region, channel, storeId }}
            aiReply={aiReply}
            onKnowledgesUpdate={(knowledges) => {
              setKnowledgeUpdate(knowledges.flat());
            }}
          />
          {conversation && (
            <>
              <Divider className="!my-4" />
              <SubmitAnswer
                action={action}
                conversation={conversation}
                replyMessage={message}
                setAction={setAction}
                region={region}
                channel={channel}
                storeId={storeId}
                answers={inputAnswers}
                onAnswersChange={setInputAnswers}
                selectedMessages={selectedMessages}
                onSelectedMessagesChange={setSelectedMessages}
              />
            </>
          )}
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 w-full h-[56px] px-4 bg-[var(--fett-bg-elevated)] border-t border-solid border-[var(--fett-border-default)] shadow flex items-center justify-end gap-4">
        <Button onClick={hideAdjustRelateKnowledge}>{t('退出')}</Button>
        <Button ghost loading={isTestLoading} type="primary" onClick={handleTest}>
          {t('测试')}
        </Button>
        {canSubmit && (
          <>
            <Button type="primary" loading={isSaveLoading} onClick={() => saveContent()}>
              {t('保存修改内容')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
